import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// ─── SEO Component ──────────────────────────────────────────────────────────────
function SEOHead() {
  useEffect(() => {
    // Page Title
    document.title = "DonatePE - Online Donation Platform";
   
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
 
    // Meta Description
    setMeta("description", "DonatePE is a trusted online donation and fundraising platform. Support causes and make a difference today.");
    
    // Robots
    setMeta("robots", "index, follow");
    
    // Charset
    let charset = document.querySelector('meta[charset="UTF-8"]');
    if (!charset) {
      charset = document.createElement("meta");
      charset.setAttribute("charset", "UTF-8");
      document.head.insertBefore(charset, document.head.firstChild);
    }
    
    // Viewport
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement("meta");
      viewport.setAttribute("name", "viewport");
      viewport.setAttribute("content", "width=device-width, initial-scale=1.0");
      document.head.appendChild(viewport);
    }
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://donatepe.org/");
    
    // Open Graph Tags
    setMeta("og:title", "DonatePE - Online Donation Platform", true);
    setMeta("og:description", "DonatePE is a trusted online donation and fundraising platform. Support causes and make a difference today.", true);
    setMeta("og:url", "https://donatepe.org/", true);
    setMeta("og:type", "website", true);
    setMeta("og:image", "https://donatepe.org/logo.png", true);
    
    // Twitter Card Tags
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", "DonatePE - Online Donation Platform");
    setMeta("twitter:description", "DonatePE is a trusted online donation and fundraising platform. Support causes and make a difference today.");
    setMeta("twitter:image", "https://donatepe.org/logo.png");
  }, []);
 
  return null;
}
 
// ─── Icons ─────────────────────────────────────────────────────────────────────
const CheckCircleIcon = () => (
  <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
 
const ShieldCheckIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
 
const RocketIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="M12 15v3m-3-8H7.5a4.5 4.5 0 1 1 0-9h9a4.5 4.5 0 1 1 0 9H15M9 6h6" />
    <path d="M12 15l3-3" />
  </svg>
);
 
const UsersIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.972 5.972 0 0 0-.841-2.373m-7.022 2.373a5.972 5.972 0 0 1 .841-2.373m0 0a7.5 7.5 0 0 0-4.18-1.446 6.99 6.99 0 0 0-3.519.955m7.699-2.409a4.5 4.5 0 0 1-2.16-3.861m4.5 3.861a4.5 4.5 0 0 0 2.16-3.861M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9z" />
  </svg>
);
 
const HeartHandIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
  </svg>
);
 
const ClockIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
 
const WalletIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
    <circle cx="16" cy="15" r="1" />
  </svg>
);
 
const EyeIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
  </svg>
);
 
const HeadphonesIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
  </svg>
);
 
const LockIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
 
const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
 
// ─── Hero Section ────────────────────────────────────────────────────────────────
function Hero({ onStartFundraiser, onGetCallback }) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="absolute top-[-150px] right-[-100px] w-[400px] h-[400px] rounded-full bg-emerald-100/30 blur-3xl" />
      <div className="absolute bottom-[-100px] left-[-100px] w-[350px] h-[350px] rounded-full bg-teal-100/20 blur-3xl" />
     
      <div className="px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-20 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
           
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-emerald-200 rounded-full px-4 py-1.5 shadow-sm">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-sm font-medium text-emerald-700">Medical Crowdfunding on DonatePe</span>
              </div>
 
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 leading-tight mt-5">
                Get Financial Help
                <br />
                <span className="bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent">
                  for Medical Treatment
                </span>
              </h1>
 
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mt-5">
                Medical emergencies can happen anytime — but money should never stop life-saving treatment.
                With DonatePe, you can raise funds quickly to cover hospital bills, surgeries, medicines,
                ICU care, and more. Thousands of people are ready to help. All you need to do is take the first step.
              </p>
 
              <div className="flex flex-wrap gap-3 justify-center lg:justify-start mt-6">
                <button
                  onClick={onStartFundraiser}
                  className="group bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold px-5 py-2.5 rounded-xl text-sm sm:text-base transition-all duration-300 hover:shadow-xl hover:scale-105 transform shadow-md flex items-center gap-1.5"
                >
                  START A FREE FUNDRAISER
                  <ArrowRightIcon />
                </button>
                <button
                  onClick={onGetCallback}
                  className="bg-white text-emerald-600 font-semibold border-2 border-emerald-200 px-5 py-2.5 rounded-xl text-sm sm:text-base transition-all duration-300 hover:bg-emerald-50 hover:border-emerald-300 hover:shadow-md"
                >
                  GET A CALLBACK
                </button>
              </div>
            </div>
 
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-200/40">
                <img
                  src="assets/emotionalimage.png"
                  alt="Patient in hospital with emotional family member"
                  className="w-full h-[280px] sm:h-[320px] md:h-[350px] lg:h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
 
// ─── Why Choose Section ──────────────────────────────────────────────────────────
function WhyChoose() {
  const features = [
    { icon: <ShieldCheckIcon />, title: "Trusted by families across India", color: "emerald" },
    { icon: <WalletIcon />, title: "Fast and secure donation system", color: "blue" },
    { icon: <HeadphonesIcon />, title: "24/7 support team to guide you", color: "purple" },
    { icon: <RocketIcon />, title: "Instant fundraiser creation", color: "orange" },
    { icon: <ClockIcon />, title: "Quick withdrawal anytime", color: "pink" },
    { icon: <EyeIcon />, title: "100% transparent process", color: "teal" },
  ];
 
  const colorClasses = {
    emerald: "from-emerald-500 to-emerald-600",
    blue: "from-blue-500 to-blue-600",
    purple: "from-purple-500 to-purple-600",
    orange: "from-orange-500 to-orange-600",
    pink: "from-pink-500 to-pink-600",
    teal: "from-teal-500 to-teal-600",
  };
 
  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">Why Choose DonatePe?</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full mx-auto" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {features.map((f, i) => (
              <div
                key={i}
                className="group relative bg-white rounded-xl p-4 md:p-5 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gradient-to-br ${colorClasses[f.color]} text-white flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  {f.icon}
                </div>
                <h3 className="text-sm md:text-base font-semibold text-gray-900">{f.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
 
// ─── What is Medical Crowdfunding ─────────────────────────────────────────────────
function WhatIs() {
  const points = [
    "No repayment required",
    "No financial burden",
    "100% funds go to treatment"
  ];
 
  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-slate-50 to-gray-50">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="assets/crowdfunding.png"
                  alt="Medical treatment fundraising support on DonatePE"
                  className="w-full h-[300px] md:h-[350px] lg:h-[320px] object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -right-4 md:-bottom-6 md:-right-6 bg-white rounded-xl shadow-lg p-3 md:p-4 border border-emerald-100 max-w-[140px] md:max-w-[170px]">
                <div className="text-2xl md:text-3xl font-bold text-emerald-600 mb-0.5">₹0</div>
                <div className="text-xs text-gray-600">No repayment needed</div>
              </div>
            </div>
            <div className="order-1 lg:order-2 space-y-5 md:space-y-6 text-center lg:text-left">
              <div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">What is Medical Crowdfunding?</h2>
                <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                  Medical crowdfunding is a way to collect small donations from many people online to support someone's treatment.
                </p>
              </div>
              <div className="space-y-2 md:space-y-3">
                {points.map((point, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 md:p-3 bg-white rounded-lg shadow-sm justify-center lg:justify-start">
                    <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircleIcon />
                    </div>
                    <span className="text-sm md:text-base font-medium text-gray-800">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
 
// ─── How It Works Section ────────────────────────────────────────────────────────
function HowItWorks() {
  const steps = [
    { num: "01", title: "Start Your Fundraiser", desc: "Create your fundraiser in minutes.", gradient: "from-emerald-500 to-teal-500" },
    { num: "02", title: "Share Everywhere", desc: "Share on social media.", gradient: "from-blue-500 to-cyan-500" },
    { num: "03", title: "Receive Donations", desc: "Start getting donations instantly.", gradient: "from-purple-500 to-pink-500" },
    { num: "04", title: "Withdraw Funds Anytime", desc: "Access funds anytime.", gradient: "from-orange-500 to-red-500" },
  ];
 
  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">How DonatePe Works</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full mx-auto mb-3 md:mb-4" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {steps.map((step, i) => (
              <div key={step.num} className="relative group text-center">
                <div className={`w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-xl bg-gradient-to-br ${step.gradient} text-white flex items-center justify-center text-lg md:text-xl font-bold shadow-md group-hover:scale-110 transition-transform duration-300`}>
                  {step.num}
                </div>
                <h3 className="text-base md:text-lg font-bold text-gray-900 mb-1 md:mb-2">{step.title}</h3>
                <p className="text-gray-500 text-xs md:text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
 
// ─── Who Can Start Section ───────────────────────────────────────────────────────
function WhoCanStart() {
  const categories = [
    { icon: <UsersIcon />, title: "For Yourself" },
    { icon: <HeartHandIcon />, title: "For Family" },
    { icon: <ShieldCheckIcon />, title: "For Friends" },
  ];
 
  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">Who Can Start a Fundraiser?</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full mx-auto mb-3 md:mb-4" />
            <p className="text-base md:text-lg text-gray-600">Anyone can start a fundraiser — for themselves, family, or friends.</p>
          </div>
         
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6 max-w-4xl mx-auto">
            {categories.map((cat, i) => (
              <div
                key={i}
                className="group bg-white rounded-xl p-5 md:p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform duration-300">
                  {cat.icon}
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900">{cat.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
 
// ─── Treatments Section ──────────────────────────────────────────────────────────
function Treatments() {
  const treatments = [
    "Cancer treatment", "Heart surgery", "Organ transplant",
    "ICU care", "Kidney Dialysis", "Brain Surgery", "and more"
  ];
 
  return (
    <section className="py-16 md:py-20 bg-gray-50">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">Treatments You Can Raise Funds For</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full mx-auto mb-6 md:mb-8" />
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3 justify-center">
            {treatments.map((t, i) => (
              <span
                key={i}
                className="px-3 md:px-5 py-1.5 md:py-2 bg-white rounded-full text-gray-700 font-medium shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 cursor-default border border-gray-100 text-xs md:text-sm"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
 
// ─── Trust Section ────────────────────────────────────────────────────────────────
function Trust() {
  const trustItems = [
    { icon: <LockIcon />, title: "Secure payment gateways", desc: "256-bit SSL encryption" },
    { icon: <ShieldCheckIcon />, title: "Verified campaigns", desc: "100% verification process" },
    { icon: <EyeIcon />, title: "Transparent tracking", desc: "Real-time updates" },
    { icon: <HeadphonesIcon />, title: "Dedicated support team", desc: "Available 24/7" },
  ];
 
  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-gray-900 to-gray-800">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3">Safe, Secure & Trusted</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full mx-auto mb-3 md:mb-4" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {trustItems.map((item, i) => (
              <div
                key={i}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-5 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 mb-3">
                  {item.icon}
                </div>
                <h3 className="text-sm md:text-base font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-gray-300 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
 
// ─── CTA Section ─────────────────────────────────────────────────────────────────
function CTA({ onStartFundraiser, onGetCallback }) {
  return (
    <section id="cta" className="py-16 md:py-20 relative overflow-hidden bg-gradient-to-br from-emerald-600 to-teal-700">
      <div className="absolute inset-0 bg-black/20" />
      <div className="absolute top-[-80px] right-[-80px] w-64 h-64 rounded-full bg-white/10 blur-3xl animate-pulse" />
      <div className="absolute bottom-[-80px] left-[-80px] w-64 h-64 rounded-full bg-white/10 blur-3xl" />
     
      <div className="px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-emerald-100 text-sm md:text-base mb-2 font-medium">You Are Not Alone</p>
          <h2 className="text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-3 md:mb-4">
            Thousands of donors are ready to support patients in need.
          </h2>
          <div className="w-20 md:w-24 h-0.5 bg-white/30 rounded-full mx-auto my-3 md:my-4" />
          <p className="text-emerald-100 text-base md:text-lg mb-2 md:mb-3 font-semibold">Start Now</p>
         
          <div className="flex flex-wrap gap-3 md:gap-4 justify-center">
            <button
              onClick={onStartFundraiser}
              className="bg-white text-emerald-600 font-bold px-6 md:px-8 py-2.5 md:py-3 rounded-xl text-sm md:text-base transition-all duration-300 hover:shadow-xl hover:scale-105 transform"
            >
              START A FREE FUNDRAISER
            </button>
            <button
              onClick={onGetCallback}
              className="bg-transparent text-white font-bold border-2 border-white px-6 md:px-8 py-2.5 md:py-3 rounded-xl text-sm md:text-base transition-all duration-300 hover:bg-white/10 hover:scale-105 transform"
            >
              GET A CALLBACK
            </button>
          </div>
         
          <div className="mt-5 md:mt-6 flex flex-wrap items-center justify-center gap-4 md:gap-5 text-emerald-100 text-xs">
            <span>✓ No fees to start</span>
            <span>✓ Instant fund access</span>
            <span>✓ 24/7 support</span>
          </div>
        </div>
      </div>
    </section>
  );
}
 
// ─── Main App Component ──────────────────────────────────────────────────────────
export default function App() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  // Check for logged in user
  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (err) {
        setUser(null);
      }
    }
  }, []);

  // Handle Start Fundraiser click based on user login status
  const handleStartFundraiser = () => {
    if (user) {
      // Logged in user - go to detailsScreen
      navigate("/detailsScreen", {
        state: {
          userId: user.id || user.userId
        }
      });
    } else {
      // Non-logged in user - go to start-fundraiser
      localStorage.removeItem("preselectedPurpose");
      navigate("/start-fundraiser");
    }
  };

  // Handle Get a Callback
  const handleGetCallback = () => {
    navigate("/contact");
  };

  return (
    <>
      <SEOHead />
      {/* Organization Schema JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "DonatePE - Crowdfunding & Donation Platform",
          "url": "https://donatepe.org",
          "logo": "https://donatepe.org/logo.png",
          "description": "DonatePE is a trusted online donation and fundraising platform."
        })}
      </script>
      <div className="font-sans antialiased">
        <Hero onStartFundraiser={handleStartFundraiser} onGetCallback={handleGetCallback} />
        <WhyChoose />
        <WhatIs />
        <HowItWorks />
        <WhoCanStart />
        <Treatments />
        <Trust />
        <CTA onStartFundraiser={handleStartFundraiser} onGetCallback={handleGetCallback} />
      </div>
    </>
  );
}